// Tenant-aware API utilities for multi-tenant application

// Store the original fetch function
const originalFetch = window.fetch;

/**
 * Get the tenant ID for a given email/username
 * This should be called client-side to determine which tenant header to send
 * 
 * IMPORTANT: Update this mapping to match your server-side UsernameToTenantMap
 */
export const getTenantForEmail = (email: string): string | null => {
  // Hardcoded mapping - matches server-side UsernameToTenantMap
  // TODO: Replace with API call when tenant configuration is database-backed
  const emailToTenantMap: Record<string, string> = {
    'user@example.com': 'ppm-easy_tester',
    // Add more email -> tenant mappings here
    // Example: 'jane@company.com': 'tenant1',
  };

  return emailToTenantMap[email.toLowerCase()] || null;
};

/**
 * Store tenant ID in session storage for subsequent requests
 */
export const setTenantInSession = (tenantId: string) => {
  sessionStorage.setItem('X-Tenant-Id', tenantId);
};

/**
 * Get tenant ID from session storage
 */
export const getTenantFromSession = (): string | null => {
  return sessionStorage.getItem('X-Tenant-Id');
};

/**
 * Clear tenant from session storage (e.g., on logout)
 */
export const clearTenantFromSession = () => {
  sessionStorage.removeItem('X-Tenant-Id');
};

/**
 * Enhanced fetch wrapper that automatically includes tenant header
 */
export const fetchWithTenant = async (
  url: string,
  options: RequestInit = {}
): Promise<Response> => {
  const tenantId = getTenantFromSession();
  
  const headers = new Headers(options.headers || {});
  
  // Add tenant header if available
  if (tenantId) {
    headers.set('X-Tenant-Id', tenantId);
  }
  
  // Ensure credentials are included for authentication
  const enhancedOptions: RequestInit = {
    ...options,
    headers,
    credentials: 'include',
  };
  
  return fetch(url, enhancedOptions);
};

/**
 * Login with tenant resolution
 */
export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  message: string;
  tenantId: string;
}

export interface TenantOption {
  tenantId: string;
  tenantName: string;
  isActive: boolean;
}

export interface AdminLoginResponse {
  isAdmin: boolean;
  adminId: string;
  adminName: string;
  availableTenants: TenantOption[];
}

export interface MultiTenantLoginResponse {
  requiresTenantSelection: boolean;
  availableTenants: TenantOption[];
}

export const loginWithTenant = async (
  email: string,
  password: string,
  rememberMe: boolean = false
): Promise<LoginResponse | AdminLoginResponse | MultiTenantLoginResponse> => {
  // The backend will automatically resolve the tenant from the email
  // We just need to call the login endpoint
  const loginUrl = rememberMe 
    ? '/Account/login?useCookies=true' 
    : '/Account/login?useSessionCookies=true';

  const response = await fetch(loginUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({
      email,
      password,
      rememberMe,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Login failed' }));
    throw new Error(error.message || 'Login failed');
  }

  const data = await response.json();
  
  // Check if this is an admin login response
  if (data.isAdmin === true) {
    return data as AdminLoginResponse;
  }
  
  // Check if this requires tenant selection
  if (data.requiresTenantSelection === true) {
    return data as MultiTenantLoginResponse;
  }
  
  // Regular tenant user login - store tenant ID for subsequent requests
  if (data.tenantId) {
    setTenantInSession(data.tenantId);
  }
  
  return data as LoginResponse;
};

export const completeLogin = async (tenantId: string): Promise<LoginResponse> => {
  const response = await fetch('/Account/complete-login', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({ tenantId }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Login completion failed' }));
    throw new Error(error.message || 'Login completion failed');
  }

  const data = await response.json();
  
  // Store tenant ID for subsequent requests
  if (data.tenantId) {
    setTenantInSession(data.tenantId);
  }
  
  return data as LoginResponse;
};

/**
 * Logout and clear tenant session
 */
export const logout = async (): Promise<void> => {
  await fetch('/logout', {
    method: 'POST',
    credentials: 'include',
  });
  
  clearTenantFromSession();
};

/**
 * Get current user info (includes tenant information)
 */
export const getCurrentUser = async () => {
  const response = await fetchWithTenant('/pingauth');
  
  if (!response.ok) {
    throw new Error('Failed to get user info');
  }
  
  return response.json();
};

/**
 * Install global fetch interceptor to automatically add tenant headers
 * Call this once at app startup (e.g., in main.tsx or App.tsx)
 */
export const installTenantInterceptor = () => {
  window.fetch = function(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    const tenantId = getTenantFromSession();
    
    // Only add tenant header to relative URLs (internal API calls)
    const isRelativeUrl = typeof input === 'string' && !input.startsWith('http');
    
    if (tenantId && isRelativeUrl) {
      const headers = new Headers(init?.headers || {});
      headers.set('X-Tenant-Id', tenantId);
      
      const enhancedInit: RequestInit = {
        ...init,
        headers,
        credentials: init?.credentials || 'include',
      };
      
      return originalFetch(input, enhancedInit);
    }
    
    // For external URLs or when no tenant, use original fetch
    return originalFetch(input, init);
  };
};

/**
 * Uninstall the global fetch interceptor (for cleanup/testing)
 */
export const uninstallTenantInterceptor = () => {
  window.fetch = originalFetch;
};
